// Per-biome ambient weather — cheap drifting motes (dust, blowing sand, floating spores,
// rising embers) that surround the player so each world feels alive in the air, not just on
// the ground. Pure params picker (headless-testable); the renderer pools a fixed THREE.Points
// layer and animates it from these. Render-only — no sim/determinism impact.

export interface WeatherParams {
  /** how many motes are active (pooled, capped by the renderer) */
  count: number
  /** mote tint (hex) */
  color: number
  /** point size */
  size: number
  /** horizontal drift speed (wind), world units/sec */
  drift: number
  /** vertical velocity: >0 rises (embers/spores), <0 settles (dust/sand) */
  rise: number
  /** spawn box half-extent around the player (world units) */
  area: number
  /** vertical span the motes occupy (world units) */
  height: number
  /** mote opacity 0..1 */
  opacity: number
}

// calm plains — sparse pale dust, slow drift, gently settling
const MEADOW: WeatherParams = { count: 70, color: 0xbfc6d8, size: 0.16, drift: 1.2, rise: -0.25, area: 46, height: 16, opacity: 0.32 }
// desert — lots of warm sand blowing near-horizontally and fast
const DUNES: WeatherParams = { count: 130, color: 0xd9b27a, size: 0.18, drift: 4.5, rise: -0.6, area: 48, height: 12, opacity: 0.4 }
// graveyard — cold ashen spores drifting slowly upward (eerie)
const GRAVEYARD: WeatherParams = { count: 90, color: 0x9fb9b0, size: 0.17, drift: 0.9, rise: 0.5, area: 46, height: 18, opacity: 0.34 }
// rift — bright magenta embers/sparks rising fast
const RIFT: WeatherParams = { count: 110, color: 0xff5cc0, size: 0.2, drift: 1.6, rise: 1.6, area: 48, height: 22, opacity: 0.5 }

const WEATHER: Record<string, WeatherParams> = { meadow: MEADOW, dunes: DUNES, graveyard: GRAVEYARD, rift: RIFT }

/** The per-biome weather params for a map id; the calm meadow dust is the default. */
export function weatherParams(mapId: string): WeatherParams {
  return WEATHER[mapId] ?? MEADOW
}
